import { getCurrentIntlLocale } from "../localization";

export type UnitSystem = "metric" | "imperial";

const METERS_TO_KILOMETERS = 1 / 1000;
const METERS_TO_MILES = 0.000621371192;
const METERS_TO_FEET = 3.280839895;
const METERS_PER_SECOND_TO_KMH = 3.6;
const METERS_PER_SECOND_TO_MPH = 2.236936292;

const asFiniteNumber = (value: number | null | undefined): number | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }

  return value;
};

const formatNumericValue = (
  value: number,
  options?: Pick<Intl.NumberFormatOptions, "maximumFractionDigits" | "minimumFractionDigits">
): string => {
  return new Intl.NumberFormat(getCurrentIntlLocale(), {
    maximumFractionDigits: options?.maximumFractionDigits ?? 1,
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
  }).format(value);
};

export const getDistanceUnitLabel = (unitSystem: UnitSystem): "km" | "mi" => {
  return unitSystem === "imperial" ? "mi" : "km";
};

export const getSpeedUnitLabel = (unitSystem: UnitSystem): "km/h" | "mph" => {
  return unitSystem === "imperial" ? "mph" : "km/h";
};

export const getElevationUnitLabel = (unitSystem: UnitSystem): "m" | "ft" => {
  return unitSystem === "imperial" ? "ft" : "m";
};

export const convertDistanceFromMeters = (
  meters: number | null | undefined,
  unitSystem: UnitSystem
): number | null => {
  const value = asFiniteNumber(meters);
  if (value === null) {
    return null;
  }

  if (unitSystem === "imperial") {
    return value * METERS_TO_MILES;
  }

  return value * METERS_TO_KILOMETERS;
};

export const convertSpeedFromMetersPerSecond = (
  metersPerSecond: number | null | undefined,
  unitSystem: UnitSystem
): number | null => {
  const value = asFiniteNumber(metersPerSecond);
  if (value === null) {
    return null;
  }

  if (unitSystem === "imperial") {
    return value * METERS_PER_SECOND_TO_MPH;
  }

  return value * METERS_PER_SECOND_TO_KMH;
};

export const convertElevationFromMeters = (
  meters: number | null | undefined,
  unitSystem: UnitSystem
): number | null => {
  const value = asFiniteNumber(meters);
  if (value === null) {
    return null;
  }

  if (unitSystem === "imperial") {
    return value * METERS_TO_FEET;
  }

  return value;
};

type UnitFormatOptions = {
  fallback?: string;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
};

export const formatDistanceFromMeters = (
  meters: number | null | undefined,
  unitSystem: UnitSystem,
  options?: UnitFormatOptions
): string => {
  const convertedValue = convertDistanceFromMeters(meters, unitSystem);
  if (convertedValue === null) {
    return options?.fallback || "TBD";
  }

  const formattedValue = formatNumericValue(convertedValue, {
    maximumFractionDigits: options?.maximumFractionDigits ?? 1,
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
  });

  return `${formattedValue} ${getDistanceUnitLabel(unitSystem)}`;
};

export const formatSpeedFromMetersPerSecond = (
  metersPerSecond: number | null | undefined,
  unitSystem: UnitSystem,
  options?: UnitFormatOptions
): string => {
  const convertedValue = convertSpeedFromMetersPerSecond(metersPerSecond, unitSystem);
  if (convertedValue === null) {
    return options?.fallback || "TBD";
  }

  const formattedValue = formatNumericValue(convertedValue, {
    maximumFractionDigits: options?.maximumFractionDigits ?? 1,
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
  });

  return `${formattedValue} ${getSpeedUnitLabel(unitSystem)}`;
};

export const formatElevationFromMeters = (
  meters: number | null | undefined,
  unitSystem: UnitSystem,
  options?: UnitFormatOptions
): string => {
  const convertedValue = convertElevationFromMeters(meters, unitSystem);
  if (convertedValue === null) {
    return options?.fallback || "TBD";
  }

  const formattedValue = formatNumericValue(convertedValue, {
    maximumFractionDigits: options?.maximumFractionDigits ?? 0,
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
  });

  return `${formattedValue} ${getElevationUnitLabel(unitSystem)}`;
};

// Both GPX-calculated totals and organizer-entered values use these race fields.
// Missing totals are hidden; a real zero is still a valid course measurement.
export const formatCourseElevationFromMeters = (
  meters: number | null | undefined,
  unitSystem: UnitSystem
): string | null => {
  const value = convertElevationFromMeters(meters, unitSystem);
  if (value === null || !Number.isFinite(value) || value < 0) return null;
  return `${formatNumericValue(value)} ${getElevationUnitLabel(unitSystem)}`;
};
